import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import injectSheet from 'react-jss';

import {
  downloadAttachment,
  downloadDocumentAttachment,
  uploadAttachments,
} from '~/api/document';
import { EM_DASH } from '~/common/constants/unicode';
import { showToast } from '~/components/Shared/EcToast/showToast';
import FieldValuesMultiSelect from '~/components/Shared/FieldValuesMultiSelect/FieldValuesMultiSelect';
import FieldValuesSingleSelect from '~/components/Shared/FieldValuesSingleSelect/FieldValuesSingleSelect';
import { Button } from '~/eds/src/components/Button/Button';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { NumberInput } from '~/eds/src/components/NumberInput/NumberInput';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { parseDate } from '~/eds/src/utils/parseDate';
import { AlgorithmStatusType } from '~/enums/AlgorithmStatusType';
import { DataFieldType } from '~/enums/DataFieldType';
import { isWssField } from '~/features/document-handler/utils';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { withFormatNumber } from '~/i18n/hocs/withFormatNumber';
import { parseUserDate } from '~/i18n/utils';
import * as toastTypes from '~/types/toast.types';
import Box from '~/ui/components/Box/Box';
import FileInput from '~/ui/components/FileInput/FileInput';
import FileLabel from '~/ui/components/FileLabel/FileLabel';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import { getUniqueFieldValues } from '~/utils/document.utils';
import { downloadFile } from '~/utils/files';

import { black5 } from '../../../assets/shared-styles/general';
import EcCard from '../../Shared/EcCard/EcCard';
import EcDatepicker from '../../Shared/EcDatepicker/EcDatepicker';
import { coerceInputValueToNumber } from '../../Shared/EcFilterField/EcFilterField.utils';
import EcInput from '../../Shared/EcInput/EcInput';
import EcMultipleSelectValue from '../../Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import EcSelect from '../../Shared/EcSelect/EcSelect';
import SubArrowIcon from '../../Shared/Icons/SubArrowIcon';
import { editDocument } from '../Document.data';
import styles from './DocumentOverviewPanel.styles';
import {
  booleanOptions,
  capitalizeString,
  preprocessEditData,
  processSectionItems,
} from './DocumentOverviewPanel.utils';

const ecCardContentStyles = { display: 'flex', padding: '16px' };
class DocumentOverviewPanel extends Component {
  constructor(props) {
    super(props);
    this.setInitialState = this.setInitialState.bind(this);
    this.handleOnSaveChangesClick = this.handleOnSaveChangesClick.bind(this);
    this.handleOnPartiesShowAllClick =
      this.handleOnPartiesShowAllClick.bind(this);
    this.state = {
      editMode: false,
      editData: {},
      isSaving: false,
      showAllParties: false,
    };
  }
  componentDidMount() {
    this.setInitialState();
  }
  setInitialState() {
    const { formatDate } = this.props;

    this.setState({
      editData: processSectionItems(this.props.sections, formatDate),
    });
  }

  isWssField = (field) => {
    const { flags } = this.props;
    return flags[FlagType.WSSDesignatedWorkflow] && isWssField(field);
  };

  handleOnSaveChangesClick() {
    const { intl } = this.props;
    const requestData = preprocessEditData(this.state.editData);
    this.setState({ isSaving: true });
    editDocument(this.props.documentId, requestData)
      .catch(() => {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'DocumentOverviewPanel.editFieldsError',
            defaultMessage: 'An error occurred while editing the field(s).',
            description: 'Error message when editing document fields fails',
          }),
        );
      })
      .finally(() => {
        this.setState({ isSaving: false, editMode: false });
        this.props.onEditSubmitted();
      });
  }
  handleOnPartiesShowAllClick() {
    this.setState({ showAllParties: true });
  }
  downloadFileAttachment(fileObject) {
    const { documentId, flags, intl } = this.props;
    const downloadFn = flags[FlagType.DownloadDocumentAttachment]
      ? () => downloadDocumentAttachment(documentId, fileObject.value)
      : () => downloadAttachment(fileObject.value);

    downloadFn()
      .then((file) => {
        showToast(
          toastTypes.DOWNLOAD,
          intl.formatMessage(
            {
              id: 'DocumentOverviewPanel.downloadSuccess',
              defaultMessage: '{fileName} successfully downloaded.',
              description:
                'Success message when file download completes. Example: fileName = "contract.pdf"',
            },
            { fileName: fileObject.display_value },
          ),
        );
        downloadFile(file, fileObject.display_value);
      })
      .catch(() =>
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'DocumentOverviewPanel.downloadError',
            defaultMessage: 'Download attachment failed. Please try again.',
            description: 'Error message when file download fails',
          }),
        ),
      );
  }
  handleFileAttachError() {
    const { intl } = this.props;
    showToast(
      toastTypes.ERROR,
      intl.formatMessage({
        id: 'DocumentOverviewPanel.attachFileError',
        defaultMessage: 'Attach file failed. Please try again.',
        description: 'Error message when file attachment fails',
      }),
    );
  }
  filterSuggestions(input, options) {
    return options.filter((i) =>
      i.label.toLowerCase().includes(input.toLowerCase()),
    );
  }
  renderBooleanItem(item) {
    const { classes } = this.props;
    if (this.state.editMode) {
      let defaultValue = booleanOptions[0];
      if (item.value === null) {
        defaultValue = null;
      } else if (!item.value.value) {
        defaultValue = booleanOptions[1];
      }
      return (
        <div className={classes.editItemContainer}>
          <EcSelect
            aria-label={item.name}
            defaultValue={defaultValue}
            options={booleanOptions}
            onChange={(newValue) => {
              const newState = {};
              newState[item.id] = newValue ? newValue.value : newValue;
              this.setState({
                editData: { ...this.state.editData, ...newState },
              });
            }}
            isDisabled={!item.is_editable || this.isWssField(item)}
            isClearable={item.is_nullable}
            isSearchable={false}
          />
        </div>
      );
    }
    return item.value
      ? capitalizeString(item.value.display_value.toString())
      : EM_DASH;
  }
  renderNumberItem(item) {
    const { classes, intl, formatNumber } = this.props;
    const presentValue = this.state.editData[item.id];
    if (this.state.editMode) {
      return (
        <div className={classes.editItemContainer}>
          <NumberInput
            name={item.name}
            value={coerceInputValueToNumber(presentValue)}
            onChange={(updatedValue) => {
              const newState = {};
              newState[item.id] = coerceInputValueToNumber(updatedValue);
              this.setState({
                editData: { ...this.state.editData, ...newState },
              });
            }}
            disabled={!item.is_editable || this.isWssField(item)}
            placeholder={intl.formatMessage({
              id: 'DocumentOverviewPanel.enterAmount',
              defaultMessage: 'enter amount',
              description: 'Placeholder text for number input field',
            })}
          />
        </div>
      );
    }
    return formatNumber(item?.value?.value);
  }

  handleDateChange = (date, item, dateFormat) => {
    const { formatDate } = this.props;
    const newState = {};
    newState[item.id] = date ? formatDate(date, dateFormat) : null;
    this.setState((prevState) => ({
      editData: { ...prevState.editData, ...newState },
    }));
  };

  renderLocalizedDateInput(item) {
    const { classes } = this.props;
    const date = this.state.editData[item.id];

    return (
      <div className={classes.editItemContainer}>
        <DateInput
          value={date ? parseUserDate(date) : null}
          onChange={(date) => this.handleDateChange(date, item, 'iso')}
          disabled={!item.is_editable || this.isWssField(item)}
          width="100%"
        />
      </div>
    );
  }

  renderLegacyDateInput(item) {
    const { classes } = this.props;
    const date = this.state.editData[item.id];

    return (
      <div className={classes.editItemContainer}>
        <EcDatepicker
          title={item.name}
          date={date ? parseDate(date) : null}
          onDateChange={(date) => this.handleDateChange(date, item, 'iso')}
          disabled={!item.is_editable || this.isWssField(item)}
          isClearable={item.is_nullable}
        />
      </div>
    );
  }

  renderEditMode(enableLocalization, item) {
    return enableLocalization
      ? this.renderLocalizedDateInput(item)
      : this.renderLegacyDateInput(item);
  }

  renderDisplayMode(item) {
    const { formatDate } = this.props;
    return formatDate(item.value?.display_value, 'long');
  }

  renderDateItem(item) {
    const { flags } = this.props;
    const enableLocalization = flags[FlagType.LanguageSelect];

    if (this.state.editMode) {
      return this.renderEditMode(enableLocalization, item);
    }

    return this.renderDisplayMode(item);
  }

  renderStringItem(item) {
    const { classes, intl } = this.props;
    const presentValue = this.state.editData[item.id];
    if (this.state.editMode) {
      return (
        <div className={classes.editItemContainer}>
          <EcInput
            ariaLabel={item.name}
            input={{
              value: presentValue || '',
              onChange: (event) => {
                const newState = {};
                newState[item.id] = event.target.value;
                this.setState({
                  editData: { ...this.state.editData, ...newState },
                });
              },
            }}
            placeholder={intl.formatMessage(
              {
                id: 'DocumentOverviewPanel.enterFieldName',
                defaultMessage: 'Enter {fieldName}',
                description:
                  'Placeholder text for string input field. Example: fieldName = "Contract Type"',
              },
              { fieldName: item.name },
            )}
            width="100%"
            isDisabled={!item.is_editable || this.isWssField(item)}
          />
        </div>
      );
    }
    return item.value ? item.value.display_value : EM_DASH;
  }
  renderTextAreaItem(item) {
    const { classes } = this.props;
    const presentValue = this.state.editData[item.id] || '';
    if (this.state.editMode) {
      return (
        <div className={classes.editItemContainer}>
          <textarea
            aria-label={item.name}
            value={presentValue}
            disabled={!item.is_editable || this.isWssField(item)}
            rows="5"
            cols="59"
            className={classes.textArea}
            onChange={(event) => {
              const newState = {};
              newState[item.id] = event.target.value;
              this.setState({
                editData: { ...this.state.editData, ...newState },
              });
            }}
          />
        </div>
      );
    }
    return item.value ? item.value.display_value : EM_DASH;
  }
  renderSingleArrayItem(item) {
    const { classes } = this.props;
    const presentValue = this.state.editData[item.id];
    if (this.state.editMode) {
      return (
        <div className={classes.editItemContainer}>
          <FieldValuesSingleSelect
            aria-label={item.name}
            defaultValue={presentValue ? presentValue : null}
            onChange={(newValue) => {
              const newState = {};
              newState[item.id] = newValue ? newValue.value : newValue;
              this.setState({
                editData: { ...this.state.editData, ...newState },
              });
            }}
            isDisabled={!item.is_editable || this.isWssField(item)}
            isClearable={item.is_nullable}
            isCreatable={item.allow_new_options}
            fieldId={item.id}
            fieldValues={item.values}
          />
        </div>
      );
    }
    return item.value ? item.value.display_value : EM_DASH;
  }
  renderMultipleValues(item) {
    const { id, is_editable } = item;
    const { classes, intl } = this.props;
    const presentValues = this.state.editData[id];
    return (
      <div className={classes.multipleValuesContainer}>
        <SubArrowIcon color={black5} />
        <div className={classes.multipleValues}>
          {presentValues.length ? (
            presentValues.map((value, index) => (
              <EcMultipleSelectValue
                label={value.label}
                key={index}
                closeHandler={() => {
                  const newState = {};
                  const newMultiState = [...this.state.editData[id]];
                  newState[id] = newMultiState.filter(
                    (multiValue) => multiValue.value !== value.value,
                  );
                  this.setState({
                    editData: { ...this.state.editData, ...newState },
                  });
                }}
                isDisabled={!is_editable || this.isWssField(item)}
              />
            ))
          ) : (
            <span className={classes.multipleValuesEmpty}>
              {intl.formatMessage({
                id: 'DocumentOverviewPanel.noValuesSelected',
                defaultMessage: 'No values selected...',
                description: 'Text shown when no values are selected',
              })}
            </span>
          )}
        </div>
      </div>
    );
  }

  renderMultipleArrayItem(item) {
    const { classes, intl } = this.props;
    const { showAllParties } = this.state;
    const presentValue = this.state.editData[item.id] || [];
    if (this.state.editMode) {
      return (
        // We use replace on data-testid because Cypress cannot handle spaces
        <div
          className={classes.editItemContainerMultiple}
          data-testid={`doc-overview-panel-${item.name.replace(/\s/g, '-')}`}
        >
          <FieldValuesMultiSelect
            aria-label={item.name}
            value={presentValue}
            onChange={(newItems) => {
              const newState = {};
              newState[item.id] = newItems;
              this.setState({
                editData: { ...this.state.editData, ...newState },
              });
            }}
            width="25%"
            placeholder={intl.formatMessage(
              {
                id: 'DocumentOverviewPanel.searchFieldName',
                defaultMessage: 'Search for {fieldName}...',
                description:
                  'Placeholder text for search field. Example: fieldName = "Contract Type"',
              },
              { fieldName: item.name },
            )}
            noOptionsMessage={intl.formatMessage(
              {
                id: 'DocumentOverviewPanel.noFieldNameFound',
                defaultMessage: 'No {fieldName} found',
                description:
                  'Message when no search results found. Example: fieldName = "Contract Type"',
              },
              { fieldName: item.name },
            )}
            isDisabled={!item.is_editable || this.isWssField(item)}
            isCreatable={item.allow_new_options}
            useMinInputLength={['Counterparties', 'Internal Parties'].includes(
              item.class_name,
            )}
            allowSelectAll
            fieldId={item.id}
            fieldValues={item.values}
          />
          {this.renderMultipleValues(item)}
        </div>
      );
    }
    if (item.value && item.value.length) {
      if (
        !showAllParties &&
        item.name === 'Counterparties' &&
        item.value.length > 2
      ) {
        return (
          <Fragment>
            <span>{item.value[0].display_value}</span>
            {' • '}
            <span>{item.value[1].display_value}</span>
            {' • '}
            <button
              className={classes.partiesShowAll}
              onClick={this.handleOnPartiesShowAllClick}
            >
              {intl.formatMessage({
                id: 'DocumentOverviewPanel.showAllParties',
                defaultMessage: 'Show All Parties',
                description: 'Button text to show all parties',
              })}
            </button>
          </Fragment>
        );
      } else {
        return item.value.map((val) => val.display_value).join(' • ');
      }
    } else {
      return EM_DASH;
    }
  }
  renderAttachmentItem(item) {
    const { intl } = this.props;
    const handleFileDataChangeHelper = (newFiles) => {
      if (!newFiles || !newFiles.length) {
        showToast(
          toastTypes.ERROR,
          intl.formatMessage({
            id: 'DocumentOverviewPanel.couldNotAttachFile',
            defaultMessage: 'Could not attach file.',
            description: 'Error message when file attachment fails',
          }),
        );
        return;
      }
      const oldItems = this.state.editData[item.id];
      const newItems = newFiles.map((fileData) => ({
        value: fileData.id,
        label: fileData.fileName + fileData.fileType,
      }));
      const newState = {};
      newState[item.id] = [...oldItems, ...newItems];
      this.setState({
        editData: { ...this.state.editData, ...newState },
      });
      showToast(
        toastTypes.SUCCESS,
        intl.formatMessage({
          id: 'DocumentOverviewPanel.attachFileSuccess',
          defaultMessage: 'Attach file succeeded.',
          description: 'Success message when file attachment succeeds',
        }),
      );
    };
    const { classes } = this.props;
    if (this.state.editMode) {
      return (
        // We use replace on data-testid because Cypress cannot handle spaces
        // TODO: refactor this Box testid to automatically apply replace
        <div
          className={classes.editItemContainerMultiple}
          data-testid={`doc-overview-panel-${item.name.replace(/\s/g, '-')}`}
        >
          <Box sx={{ maxWidth: 800 }}>
            <FileInput
              acceptedFiles="*"
              enableUpload
              enableDropzone
              enableMultiple
              onChange={(files) =>
                uploadAttachments(
                  files,
                  handleFileDataChangeHelper,
                  this.handleFileAttachError,
                )
              }
            />
          </Box>
          {this.renderMultipleValues(item)}
        </div>
      );
    }
    if (item.value?.length) {
      return (
        <FlexLayout flexDirection="column" space={5}>
          {item.value.map((fileObject) => {
            const file = new File([], fileObject.display_value);
            return (
              <Box onClick={() => this.downloadFileAttachment(fileObject)}>
                <FileLabel shouldTruncate file={file} />
              </Box>
            );
          })}
        </FlexLayout>
      );
    } else {
      return EM_DASH;
    }
  }
  renderItemValue(item) {
    let returnValue;
    switch (item.type) {
      case DataFieldType.BOOLEAN:
        returnValue = this.renderBooleanItem(item);
        break;
      case DataFieldType.DATE:
        returnValue = this.renderDateItem(item);
        break;
      case DataFieldType.NUMBER:
        returnValue = this.renderNumberItem(item);
        break;
      case DataFieldType.REFERENCE:
      case DataFieldType.STRING:
        returnValue = this.renderStringItem(item);
        break;
      case DataFieldType.TEXT_AREA:
        returnValue = this.renderTextAreaItem(item);
        break;
      case DataFieldType.ARRAY_SINGLE:
        returnValue = this.renderSingleArrayItem(item);
        break;
      case DataFieldType.ARRAY_MULTIPLE:
        returnValue = this.renderMultipleArrayItem({
          ...item,
          value: getUniqueFieldValues(item.value),
        });
        break;
      case DataFieldType.ATTACHMENT:
        returnValue = this.renderAttachmentItem(item);
        break;
      default:
        returnValue = EM_DASH;
        break;
    }
    return returnValue;
  }
  renderOverviewItem(item) {
    const { classes, intl } = this.props;
    const overviewItemClass =
      this.state.editMode &&
      [DataFieldType.ARRAY_MULTIPLE, DataFieldType.ATTACHMENT].includes(
        item.type,
      )
        ? classes.overviewItemMultiple
        : classes.overviewItem;

    return (
      <div className={overviewItemClass} key={`overviewItem-${item.name}`}>
        <div className={classes.overviewItemTitle}>
          {item.name}
          {item.is_beta ? (
            <Tooltip
              id="betaTag"
              width="260px"
              placement="bottom"
              tooltip={intl.formatMessage({
                id: 'DocumentOverviewPanel.betaTooltip',
                defaultMessage:
                  "This is a newly released intelligent field which is currently in beta testing. If you have feedback about the outputs you're seeing, please send us a note at beta@evisort.com",
                description: 'Tooltip text for beta fields',
              })}
            >
              <span className={classes.betaTooltip} data-tip data-for="betaTag">
                {intl.formatMessage({
                  id: 'DocumentOverviewPanel.betaLabel',
                  defaultMessage: 'BETA',
                  description: 'Beta label for new fields',
                })}
              </span>
            </Tooltip>
          ) : null}
          {item.help_text ? (
            <Icon
              size="s"
              icon="info"
              id={`helpTextTag-${item.id}`}
              tooltip={item.help_text}
              placement="bottom"
            />
          ) : null}
          {this.isWssField(item) ? (
            <Icon
              icon="database"
              tooltip={intl.formatMessage({
                id: 'DocumentOverviewPanel.strategicSourcingTooltip',
                defaultMessage: 'Source: Strategic Sourcing',
                description: 'Tooltip for strategic sourcing fields',
              })}
            />
          ) : null}
        </div>
        <div className={classes.overviewItemContent}>
          {this.renderItemValue(item)}
        </div>
      </div>
    );
  }

  renderSection(itemsToRender) {
    const { classes } = this.props;
    const overviewItemsArray = itemsToRender.fields.map((item) =>
      this.renderOverviewItem(item),
    );
    return (
      <div className={classes.overviewInformation}>{overviewItemsArray}</div>
    );
  }
  renderInformationSections(sections) {
    const { classes } = this.props;
    return sections.map((section, index) => (
      <Fragment key={section.title}>
        <EcCard title={section.title} contentStyles={ecCardContentStyles}>
          {this.renderSection(section)}
        </EcCard>
        {index === sections.length - 1 ? null : (
          <div className={classes.horizontalSeparator} />
        )}
      </Fragment>
    ));
  }
  renderEditCommandPanel() {
    const {
      classes,
      documentId,
      dateModified,
      flags,
      isTerminated,
      OCRProcessing,
      userVisibilityLevel,
      intl,
      formatDate,
    } = this.props;
    const { editMode } = this.state;
    const bypassEditInOverview =
      flags[FlagType.FieldValueMigrationDeferredFeaturesBypass];
    const canEdit =
      userVisibilityLevel === 'OPEN' && !bypassEditInOverview && !isTerminated;

    if (OCRProcessing?.status === AlgorithmStatusType.Failed) {
      return null;
    }
    if (OCRProcessing?.status === AlgorithmStatusType.InProgress) {
      return (
        <StatusMessage
          message={intl.formatMessage({
            id: 'DocumentOverviewPanel.processingMessage',
            defaultMessage:
              'Evisort AI is processing this document. It will be available for editing in a few minutes.',
            description: 'Message shown when document is being processed',
          })}
          action={
            canEdit
              ? {
                  text: intl.formatMessage({
                    id: 'DocumentOverviewPanel.editButton',
                    defaultMessage: 'Edit',
                    description: 'Edit button text',
                  }),
                  disabled: true,
                  icon: 'edit',
                }
              : null
          }
        />
      );
    }
    return (
      <div
        className={
          editMode ? classes.editCommandPanelActive : classes.editCommandPanel
        }
      >
        {editMode && canEdit ? (
          <Fragment>
            <div className={classes.panelText}>
              {intl.formatMessage({
                id: 'DocumentOverviewPanel.editingMessage',
                defaultMessage: 'You are currently editing this document.',
                description: 'Message shown when user is in edit mode',
              })}
            </div>
            <Button
              text={intl.formatMessage({
                id: 'DocumentOverviewPanel.discardButton',
                defaultMessage: 'Discard',
                description: 'Discard changes button text',
              })}
              onClick={() =>
                this.setState({ editMode: false }, () => this.setInitialState())
              }
              disabled={this.state.isSaving}
            />
            <span className={classes.verticalSeparator} />
            <Button
              text={intl.formatMessage({
                id: 'DocumentOverviewPanel.saveChangesButton',
                defaultMessage: 'Save Changes',
                description: 'Save changes button text',
              })}
              onClick={this.handleOnSaveChangesClick}
              isLoading={this.state.isSaving}
              variant="primary"
            />
          </Fragment>
        ) : (
          <Fragment>
            <div className={classes.panelText}>
              <FormattedMessage
                id="DocumentOverviewPanel.lastEditedMessage"
                defaultMessage="This document was last edited on <strong>{editDate}.</strong>"
                description="Message showing when document was last edited. Example: editDate = 'January 15, 2024'"
                values={{
                  strong: (chunks) => <strong>{chunks}</strong>,
                  editDate: formatDate(dateModified, 'long'),
                }}
              />
            </div>
            {canEdit ? (
              <Button
                id={`document_overviewPanel_editButton?document=${documentId}`}
                label="edit-document-overview-button"
                text={intl.formatMessage({
                  id: 'DocumentOverviewPanel.editButtonText',
                  defaultMessage: 'Edit',
                  description: 'Edit button text for document overview',
                })}
                onClick={() => this.setState({ editMode: true })}
              />
            ) : null}
          </Fragment>
        )}
      </div>
    );
  }
  render() {
    const { classes, sections } = this.props;
    return (
      <Fragment>
        {this.renderEditCommandPanel()}
        <div className={classes.horizontalSeparator} />
        {sections.length ? this.renderInformationSections(sections) : null}
      </Fragment>
    );
  }
}
DocumentOverviewPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  dateModified: PropTypes.string.isRequired,
  documentId: PropTypes.number.isRequired,
  sections: PropTypes.array.isRequired,
  onEditSubmitted: PropTypes.func.isRequired,
  userVisibilityLevel: PropTypes.string.isRequired,
  isTerminated: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  formatNumber: PropTypes.func.isRequired,
};
export default injectSheet(styles)(
  withFlags(
    injectIntl(withFormatDate(withFormatNumber(DocumentOverviewPanel))),
  ),
);
